<template>
  <div class="address-manage-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>📍 {{ $t('order.deliveryAddress') }}</h1>
      <div style="flex: 1;"></div>
    </header>

    <!-- 地址列表 -->
    <div class="address-list">
      <div v-if="loading" class="loading">
        <div class="loading-icon">⏳</div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="addresses.length === 0" class="empty">
        <div class="empty-icon">📍</div>
        <p>暂无{{ $t('order.deliveryAddress') }}</p>
        <el-button type="primary" @click="handleAdd">{{ $t('address.addAddress') }}</el-button>
      </div>

      <div v-else>
        <div v-for="addr in addresses" :key="addr.id" class="address-card" :class="{ default: addr.isDefault }">
          <div class="address-header">
            <div class="address-labels">
              <span v-if="addr.isDefault" class="badge-default">{{ $t('address.default') }}</span>
              <span class="contact-name">{{ addr.contactName }}</span>
            </div>
            <div class="address-actions">
              <el-button type="text" icon="el-icon-edit" @click="handleEdit(addr)"></el-button>
              <el-button type="text" icon="el-icon-delete" style="color:#f56c6c" @click="handleDelete(addr)"></el-button>
            </div>
          </div>
          
          <div class="address-content">
            <div class="address-row">
              <span class="label">📞</span>
              <span class="value">{{ addr.contactPhone }}</span>
            </div>
            <div class="address-row">
              <span class="label">📍</span>
              <span class="value">{{ addr.deliveryAddress }}</span>
            </div>
          </div>

          <div v-if="!addr.isDefault" class="address-footer">
            <el-button size="mini" @click="handleSetDefault(addr)">⭐ {{ $t('address.setDefault') }}</el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部添加按钮 -->
    <div class="bottom-action">
      <el-button type="primary" class="btn-add-bottom" @click="handleAdd">
        {{ $t('address.addAddress') }}
      </el-button>
    </div>

    <!-- 新增/编辑地址弹窗 -->
    <el-dialog
      :title="isEditing ? $t('address.editAddress') : $t('address.addAddress')"
      :visible.sync="dialogVisible"
      width="500px"
      :before-close="handleClose"
      :append-to-body="true"
      custom-class="address-dialog"
    >
      <el-form :model="formData" :rules="rules" ref="addressForm" label-width="100px">
        <!-- 智能识别区域 -->
        <div class="smart-parse-area">
          <el-input
            v-model="smartParseText"
            type="textarea"
            :rows="2"
            :placeholder="$t('address.smartParsePlaceholder')"
            @input="handleSmartParse"
          ></el-input>
          <div class="smart-parse-tip">
            <i class="el-icon-magic-stick"></i> {{ $t('address.smartParseTip') }}
          </div>
        </div>

        <el-form-item :label="$t('user.contact')" prop="contactName">
          <el-input v-model="formData.contactName" :placeholder="$t('address.namePlaceholder')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('delivery.contactPhone')" prop="contactPhone">
          <el-input v-model="formData.contactPhone" :placeholder="$t('address.phonePlaceholder')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('delivery.deliveryAddress')" prop="deliveryAddress">
          <el-input v-model="formData.deliveryAddress" type="textarea" :rows="3" :placeholder="$t('address.addrPlaceholder')"></el-input>
          <el-button type="primary" size="small" style="margin-top:8px" @click="showMapPicker = true">
            📍 {{ $t('address.mapPick') }}
          </el-button>
        </el-form-item>
        <el-form-item>
          <el-checkbox v-model="formData.isDefault">{{ $t('address.setDefault') }}</el-checkbox>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="handleClose">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="saveAddress">{{ isEditing ? $t('common.save') : $t('common.create') }}</el-button>
      </span>
    </el-dialog>

    <!-- 地图选点组件 -->
    <MapPicker
      v-if="showMapPicker"
      @confirm="onMapConfirmed"
      @cancel="showMapPicker = false"
    />
  </div>
</template>

<script>
import addressApi from '@/services/address';
import MapPicker from '@/components/MapPicker.vue';

export default {
  name: 'AddressManage',
  components: { MapPicker },
  data() {
    return {
      addresses: [],
      loading: false,
      dialogVisible: false,
      showMapPicker: false,
      isEditing: false,
      smartParseText: '',
      formData: {
        id: null,
        contactName: '',
        contactPhone: '',
        deliveryAddress: '',
        latitude: null,
        longitude: null,
        isDefault: false
      },
      rules: {
        contactName: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
        contactPhone: [{ required: true, message: '请输入联系电话', trigger: 'blur' }],
        deliveryAddress: [{ required: true, message: '请输入配送地址', trigger: 'blur' }]
      }
    };
  },
  created() {
    this.loadAddresses();
  },
  methods: {
    handleAdd() {
      this.isEditing = false;
      this.smartParseText = '';
      this.formData = { id: null, contactName: '', contactPhone: '', deliveryAddress: '', latitude: null, longitude: null, isDefault: false };
      this.dialogVisible = true;
    },
    handleEdit(addr) {
      this.isEditing = true;
      this.smartParseText = '';
      this.formData = {
        id: addr.id,
        contactName: addr.contactName,
        contactPhone: addr.contactPhone,
        deliveryAddress: addr.deliveryAddress,
        latitude: addr.latitude || null,
        longitude: addr.longitude || null,
        isDefault: addr.isDefault
      };
      this.dialogVisible = true;
    },
    handleClose() {
      this.dialogVisible = false;
      this.smartParseText = '';
      this.$refs.addressForm && this.$refs.addressForm.resetFields();
    },
    /**
     * 智能识别地址文本
     * 支持格式：
     *   张三 13800138000 新加坡乌节路10号
     *   张三, 13800138000, 新加坡乌节路10号
     *   张三 / 13800138000 / 新加坡乌节路10号
     */
    handleSmartParse() {
      const text = this.smartParseText.trim();
      if (!text) return;

      // 提取手机号（支持新加坡8位、中国11位、带+区号、带空格/连字符等）
      const phoneRegex = /(\+?\d{1,3}[\s\-]?)?(\d{8,11})/;
      // 提取姓名（2-4个中文字符，或英文字母）
      const nameRegex = /([\u4e00-\u9fa5]{2,4}|[A-Za-z]+(?:\s+[A-Za-z]+)?)/;

      let name = '', phone = '', address = '';

      // 尝试按分隔符拆分（支持 空格、逗号、斜杠、制表符）
      const parts = text.split(/[\s,，\/\t]+/).filter(p => p.trim());

      if (parts.length >= 3) {
        // 多段：逐个识别
        for (const part of parts) {
          const p = part.trim();
          if (!phone && phoneRegex.test(p)) {
            const match = p.match(phoneRegex);
            // 组合完整号码（区号+号码）
            phone = (match[1] || '') + match[2];
          } else if (!name && nameRegex.test(p) && p.length <= 20) {
            name = p;
          } else {
            address += (address ? ' ' : '') + p;
          }
        }
      } else {
        // 单段或两段：用正则提取
        const phoneMatch = text.match(phoneRegex);
        if (phoneMatch) {
          // 组合完整号码（区号+号码）
          phone = (phoneMatch[1] || '') + phoneMatch[2];
        }
        const nameMatch = text.match(nameRegex);
        if (nameMatch) {
          name = nameMatch[1];
        }
        // 地址 = 去掉姓名和电话后的剩余部分
        address = text
          .replace(nameMatch ? nameMatch[0] : '', '')
          .replace(phoneMatch ? phoneMatch[0] : '', '')
          .replace(/[,，\/\-]+/g, ' ')
          .trim();
      }

      // 回填表单
      if (name) this.formData.contactName = name;
      if (phone) this.formData.contactPhone = phone;
      if (address) this.formData.deliveryAddress = address;
    },
    async handleDelete(addr) {
      try {
        await this.$confirm('确定要删除这个地址吗？', '提示', { type: 'warning' });
        await addressApi.delete(addr.id);
        this.$message.success('地址已删除');
        await this.loadAddresses();
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('删除失败');
        }
      }
    },
    async handleSetDefault(addr) {
      try {
        await addressApi.setDefault(addr.id);
        this.$message.success('默认地址已设置');
        await this.loadAddresses();
      } catch (error) {
        this.$message.error('设置失败');
      }
    },
    goBack() {
      const from = this.$route.query.from || '/customer/checkout';
      this.$router.push(from);
    },
    async loadAddresses() {
      this.loading = true;
      try {
        const response = await addressApi.getList();
        this.addresses = Array.isArray(response) ? response : (response.data || []);
      } catch (error) {
        this.$message.error('加载地址失败');
      } finally {
        this.loading = false;
      }
    },
    onMapConfirmed(location) {
      this.formData.deliveryAddress = location.address;
      this.formData.latitude = location.lat;
      this.formData.longitude = location.lng;
      this.showMapPicker = false;
    },
    saveAddress() {
      this.$refs.addressForm.validate(async (valid) => {
        if (!valid) return;
        try {
          if (this.isEditing) {
            await addressApi.update(this.formData.id, this.formData);
            this.$message.success('地址已更新');
          } else {
            await addressApi.create(this.formData);
            this.$message.success('地址已添加');
          }
          this.handleClose();
          await this.loadAddresses();
        } catch (error) {
          this.$message.error(error.response?.data?.message || '保存失败');
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.address-manage-page {
  min-height: 100vh;
  background: #f0f2f5;
  padding-bottom: 80px;
}

.smart-parse-area {
  margin-bottom: 18px;
  padding: 12px;
  background: linear-gradient(135deg, #f0f4ff 0%, #faf0ff 100%);
  border-radius: 10px;
  border: 1px dashed #c4b5fd;

  .smart-parse-tip {
    margin-top: 6px;
    font-size: 12px;
    color: #7c3aed;
    i {
      margin-right: 2px;
    }
  }
}

.back-header {
  padding: calc(16px + env(safe-area-inset-top, 0px)) 16px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 100;

  .back-btn {
    font-size: 18px;
    color: #64748b;
    cursor: pointer;
    background: none;
    border: none;
    padding: 4px 8px;
  }

  h1 {
    font-size: 17px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
    flex: 1;
  }
}

.address-list {
  padding: 12px;
}

.loading,
.empty {
  text-align: center;
  background: white;
  border-radius: 14px;
  padding: 60px 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);

  .loading-icon,
  .empty-icon {
    font-size: 48px;
    margin-bottom: 12px;
  }

  p {
    color: #94a3b8;
    font-size: 14px;
    margin-bottom: 16px;
  }
}

.address-card {
  background: white;
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 2px solid transparent;
  transition: all 0.2s;

  &.default {
    border-color: #667eea;
    background: linear-gradient(135deg, #f0f4ff 0%, #faf0ff 100%);
  }
}

.address-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.address-labels {
  display: flex;
  gap: 8px;
  align-items: center;

  .badge-default {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 10px;
  }

  .contact-name {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
  }
}

.address-actions {
  display: flex;
  gap: 4px;
}

.address-content {
  .address-row {
    display: flex;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;
    align-items: flex-start;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      font-size: 14px;
      flex-shrink: 0;
    }

    .value {
      color: #475569;
      flex: 1;
      word-break: break-word;
    }
  }
}

.address-footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
}

.bottom-action {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.06);

  .btn-add-bottom {
    width: 100%;
  }
}
</style>
